import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import classNames from 'classnames';
import styles from './SeparatorsSelector.module.scss';
import { WidenImage } from '@common/Widen/WidenImage';
import { NumberInput } from '@common/NumberInput/NumberInput';
import { CHAINFLEX_MAX_BUY_LENGTH, CHAINFLEX_MIN_BUY_QUANTITY, getLocale } from '@libs/constants';
import { Button, TYPE } from '@common/Button/Button';
import { Link } from '@common/Link/Link';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { Badge, BadgeColor } from '@common/Badge/Badge';
import type { EchainPartsListItem } from '@hooks/states/echainPartsListSlice';
import { AssociationType } from '@libs/associations';
import { TooltipIcon } from '@common/TooltipIcon/TooltipIcon';
import type { WidenMediaElement } from '@libs/widen-utils';
import { MediaElementType } from '@libs/widen-utils';
import type { AssociationProductFragmentEnhanced } from '@models/enhanced-fragments';
import type { ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import { ProductHelper } from '@models/graphql';
import { NotifyPopup } from '@product/VariantSelector/NotifyPopup/NotifyPopup';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { getProductIdForLocale } from '@libs/product.helper';

export interface SeparatorsSelectorProps {
  separatorsAssociationProducts: AssociationProductFragmentEnhanced[] | null;
  onItemAdd?: (item: SeparatorItem) => void;
}

export const INIT_NUMBER_OF_ITEMS = 4;
export const INIT_NUMBER_OF_ITEMS_MD_ONLY = 3;

interface SeparatorItem {
  key: number;
  associatedProduct: AssociationProductFragmentEnhanced;
  separatorPartsListItem?: EchainPartsListItem;
  teaserImage: WidenMediaElement;
}

export const SeparatorsSelector = ({ separatorsAssociationProducts, onItemAdd }: SeparatorsSelectorProps) => {
  const router = useRouter();
  const locale = getLocale(router);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const { md, lg } = useBreakpoints();
  const { t } = useTranslation(['product/echainBuyBox', 'layout/general', 'product/buyBox']);

  const [showMoreSeparators, setShowMoreSeparators] = useState(false);
  const setSelectedInteriorSeparator = useEchainProductStore(state => state.setSelectedInteriorSeparator);
  const getEchainPartsListItem = useEchainProductStore(state => state.getEchainPartsListItem);
  const eChainPartsList = useEchainProductStore(state => state.eChainPartsList);
  const [itemCount, setItemCount] = useState<Map<number, number>>(new Map<number, number>());
  const [showPopup, setShowPopup] = useState<Map<number, boolean>>(new Map<number, boolean>());

  const standardSeparatorModelKey = getAttributeValue<string>(
    currentProduct?.attributes?.standard_separator as ProductAttribute<string>
  );

  const validProducts = separatorsAssociationProducts
    .filter(
      product => Array.isArray(product.assets) && product.assets.length > 0 && !!product.attributes?.series_description
    )
    .sort((a, b) => {
      if (a.model_id === b.model_id) {
        return 0;
      }
      if (a.model_id === standardSeparatorModelKey) {
        return -1;
      }
      if (b.model_id === standardSeparatorModelKey) {
        return 1;
      }
      return 0;
    });

  const getInitNumberOfItems = useCallback(() => {
    if (lg) {
      return INIT_NUMBER_OF_ITEMS;
    }

    if (md) {
      return INIT_NUMBER_OF_ITEMS_MD_ONLY;
    }
    return INIT_NUMBER_OF_ITEMS;
  }, [md, lg]);

  const listLength = validProducts?.length || 0;
  const maxLength = showMoreSeparators ? listLength : getInitNumberOfItems();

  const separatorItems = useMemo<SeparatorItem[]>(() => {
    return validProducts
      .filter((_, index) => index < maxLength)
      .map((product, index) => {
        const separatorPartsListItem = currentProduct ? getEchainPartsListItem(product.id) : undefined;

        const teaserImage = ProductHelper.getAssetsMedia(product).productMedia.find(
          media => media.type === MediaElementType.DRAWING
        );

        return {
          key: index,
          associatedProduct: product,
          separatorPartsListItem,
          teaserImage,
        };
      });

    // eChainPartsList is required as dependency because getEchainPartsListItem depends on it indirectly and so that the parts list is fetched and rendered correctly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentProduct,
    getEchainPartsListItem,
    maxLength,
    separatorsAssociationProducts,
    standardSeparatorModelKey,
    eChainPartsList,
  ]);

  const handleAddItemClick = async (separatorItem: SeparatorItem) => {
    if (!currentProduct || !itemCount.get(separatorItem.key)) {
      return;
    }

    await setSelectedInteriorSeparator(
      currentProduct,
      separatorItem.associatedProduct,
      AssociationType.SEPARATORS,
      locale,
      (separatorItem.separatorPartsListItem?.quantity ?? 0) + (itemCount.get(separatorItem.key) ?? 1)
    );

    setItemCount(current => {
      current.delete(separatorItem.key);
      return new Map<number, number>(current);
    });

    setShowPopup(current => {
      current.forEach((v, k) => v && current.set(k, false));
      return new Map<number, boolean>(current.set(separatorItem.key, true));
    });

    if (onItemAdd) {
      onItemAdd(separatorItem);
    }
  };

  return (
    <div className="grid grid-cols-1">
      {separatorItems && separatorItems.length > 0 && (
        <>
          <div>
            <div
              className={classNames(
                'grid ',
                'grid-cols-1',
                `sm:grid-cols-2`,
                `md:grid-cols-3`,
                `lg:grid-cols-4`,
                listLength > 1 && 'gap-8',
                'mb-4 p-2',
                'place-content-center'
              )}
            >
              {separatorItems.map(separatorItem => (
                <NotifyPopup
                  key={`${AssociationType.SEPARATORS}-element-${separatorItem.key}`}
                  type="success"
                  show={showPopup.get(separatorItem.key) ?? false}
                  onHide={() =>
                    setShowPopup(current => new Map<number, boolean>(current.set(separatorItem.key, false)))
                  }
                  content={t('ECHAIN_PRODUCT_PAGE.TOOLTIPS.ADDED_TO_PARTSLIST')}
                  containerProps={{
                    'data-testid': `${AssociationType.SEPARATORS}-element-${separatorItem.key}`,
                    className: 'px-6 pb-3 mt-6 relative border border-gray-200 bg-white flex flex-col',
                  }}
                >
                  <>
                    {separatorItem.associatedProduct.model_id === standardSeparatorModelKey && (
                      <div className={classNames('-ml-6')}>
                        <Badge text="Standard" BackgroundColor={BadgeColor.Primary} />
                      </div>
                    )}
                    {separatorItem.separatorPartsListItem && (
                      <div className="top-2 right-4 absolute">
                        <Icon id={IconID.CHECK} color={IconColor.ORANGE} size={12} primary={false} />
                      </div>
                    )}
                    <div
                      className={classNames(
                        'm-2',
                        standardSeparatorModelKey !== undefined && 'pt-14',
                        'text-center',
                        'font-semibold',
                        'text-sm',
                        'mb-0'
                      )}
                    >
                      {getAttributeValue<string>(
                        separatorItem.associatedProduct?.attributes?.series_description as ProductAttribute<string>
                      )}
                      {/* protected space with nbsp to prevent line breaks */}
                      &nbsp;
                      <TooltipIcon
                        className="align-text-top"
                        id={IconID.INFO_CIRCLE_GRAY}
                        size={5}
                        tooltipText={getAttributeValue(
                          separatorItem.associatedProduct?.attributes?.attr_description as ProductAttribute<string>
                        )}
                        iconInline
                      />
                    </div>
                    <div data-testid="product-id-display-name" className="text-center h-8 text-gray-400 mb-0 grow">
                      <small>{getProductIdForLocale(separatorItem.associatedProduct, locale)}</small>
                    </div>
                    <div className={classNames(styles.interiorSeparationImgWrapper, 'h-0 p-1 mt-8', 'relative')}>
                      {separatorItem.teaserImage && (
                        <WidenImage
                          shortCode={separatorItem.teaserImage.shortCode}
                          title={separatorItem.teaserImage.title}
                          filename={separatorItem.teaserImage.filename}
                          width={0}
                          height={0}
                          layout="fill"
                          objectFit="contain"
                        />
                      )}
                    </div>

                    <div className="mt-4 flex flex-col">
                      <NumberInput
                        onChange={value =>
                          setItemCount(current => {
                            if (value === undefined) {
                              current.delete(separatorItem.key);
                              return new Map<number, number>(current);
                            }

                            return new Map<number, number>(current.set(separatorItem.key, value));
                          })
                        }
                        className="flex-1"
                        value={itemCount.get(separatorItem.key)}
                        min={1}
                        placeholder={t('GENERAL.PLACEHOLDER.QUANTITY', { ns: 'layout/general' })}
                        max={CHAINFLEX_MAX_BUY_LENGTH}
                        errorMessage={t('BUY_BOX.UNITS_INPUT_ERRORMESSAGE', {
                          min: CHAINFLEX_MIN_BUY_QUANTITY,
                          max: CHAINFLEX_MAX_BUY_LENGTH,
                          ns: 'product/buyBox',
                        })}
                        onlyInt
                      />
                      <Button
                        disabled={!itemCount.get(separatorItem.key)}
                        icon={
                          <Icon
                            size={4}
                            color={!itemCount.get(separatorItem.key) ? IconColor.GRAY : IconColor.ORANGE}
                            id={IconID.PLUS}
                          />
                        }
                        text={t('GENERAL.ADD', { ns: 'layout/general' })}
                        type={TYPE.TERTIARY}
                        minW={false}
                        onClick={async () => {
                          await handleAddItemClick(separatorItem);
                        }}
                      />
                    </div>
                  </>
                </NotifyPopup>
              ))}
            </div>
          </div>

          {!showMoreSeparators && listLength > getInitNumberOfItems() && (
            <div className="ml-2 whitespace-nowrap flex items-center">
              <Link
                icon={IconID.DROPDOWN_DOWN}
                bigIcon
                iconPrimary
                className="uppercase text-m"
                onClick={() => {
                  setShowMoreSeparators(!showMoreSeparators);
                }}
                primary
                text={t('ECHAIN_PRODUCT_PAGE.INTERIOR_SEPARATORS.MORE_VERTICAL_ELEMENTS')}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
